import { useEffect, useMemo, useState } from "react";
import { Field, Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { differenceInCalendarMonths, format, isValid, parse } from "date-fns";

import CalendarSelectFormik from "../../../shared/Ui/Calendar/CalendarSelectFormik";
import SelectCustomFormik from "../../../shared/Ui/SelectCustom/SelectCustomFormik";
import { useAppSelector } from "../../../shared/hook";
import style from "../../EventsAudit/EventAuditList.module.css";
import {
  MarkingCodesKmStatus,
  MarkingCodesPackagingType,
  MarkingCodesReportFilters,
  createMarkingCodesReport,
} from "../../../common/api/markingCodesReportApi";
import { GetjewelryGroup } from "../../../common/api/nktApi";
import { KM_STATUS_LABEL_KEYS, PACKAGING_LABEL_KEYS } from "./markingCodesReportUtils";
import { SelectOption, useOrganisationOptions } from "../useOrganisationOptions";
import { useProductGroupOptions } from "../../../shared/hook/useProductGroupOptions";

type ProductTypeOption = SelectOption & { groupId: number };

const toApiDate = (value: string) => {
  if (!value) return undefined;
  const parsed = parse(value, "dd.MM.yyyy", new Date());
  if (!isValid(parsed)) return undefined;
  return format(parsed, "yyyy-MM-dd");
};

const activeProductGroupId = () => {
  const raw = Number(Cookies.get("activeGroup") ?? "");
  return Number.isFinite(raw) && raw > 0 ? raw : undefined;
};

function MarkingCodesStatusReportCreatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userData: any = useAppSelector((state) => state.auth.userData);

  const isOperator = userData?.selectRoleV2?.some(
    (role: { roleId: number }) => role.roleId === 5,
  );

  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [productTypes, setProductTypes] = useState<ProductTypeOption[]>([]);
  // Товарные группы с названием на языке интерфейса
  const { options: productGroupOptions } = useProductGroupOptions();
  const orgs = useOrganisationOptions(Boolean(isOperator));

  useEffect(() => {
    GetjewelryGroup()
      .then((response: any) => {
        const list = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];
        setProductTypes(
          list
            .filter((item: any) => item?.productTypeCode)
            .map((item: any) => ({
              value: String(item.productTypeCode),
              label: item.name
                ? `${item.name}: ${item.productTypeCode}`
                : String(item.productTypeCode),
              groupId: Number(item?.productGroup?.id),
            })),
        );
      })
      .catch(() => setProductTypes([]));
  }, []);

  // Коды вида товара — только выбранной товарной группы
  const productTypeOptionsFor = (groupId?: number) =>
    productTypes.filter(
      (item) => !groupId || !Number.isFinite(item.groupId) || item.groupId === groupId,
    );

  const statusOptions = useMemo(
    () =>
      Object.entries(KM_STATUS_LABEL_KEYS).map(([value, labelKey]) => ({
        value,
        label: t(labelKey),
      })),
    [t],
  );

  const packagingOptions = Object.entries(PACKAGING_LABEL_KEYS).map(
    ([value, labelKey]) => ({ value, label: t(labelKey) }),
  );

  const validate = (values: {
    dateFrom: string;
    dateTo: string;
    statuses: string[];
  }) => {
    if (!values.statuses?.length) {
      return t("marking_codes_report.statuses_required");
    }

    const from = values.dateFrom
      ? parse(values.dateFrom, "dd.MM.yyyy", new Date())
      : null;
    const to = values.dateTo
      ? parse(values.dateTo, "dd.MM.yyyy", new Date())
      : null;
    const today = new Date();

    if (from && isValid(from) && from > today) {
      return t("marking_codes_report.future_date_forbidden");
    }
    if (to && isValid(to) && to > today) {
      return t("marking_codes_report.future_date_forbidden");
    }
    if (from && to && isValid(from) && isValid(to)) {
      if (from > to) return t("billing.periodInvalidRange");
      if (differenceInCalendarMonths(to, from) > 36) {
        return t("marking_codes_report.period_max_36");
      }
    }
    return "";
  };

  const handleGenerate = async (values: {
    dateFrom: string;
    dateTo: string;
    statuses: string[];
    packagingType: string;
    ntin: string;
    productTypeCode: string;
    orderNumber: string;
    organizationId: string;
    productGroupId: string;
  }) => {
    setFormError("");
    const error = validate(values);
    if (error) {
      setFormError(error);
      return;
    }

    // Оператор — выбранная в форме ТГ, УОТ — ТГ, выбранная на главной странице
    const productGroupId = isOperator
      ? Number(values.productGroupId) || undefined
      : activeProductGroupId();
    if (!productGroupId) {
      setFormError(t("marking_codes_report.product_group_required"));
      return;
    }

    const payload: MarkingCodesReportFilters = {
      productGroupId,
      dateFrom: toApiDate(values.dateFrom),
      dateTo: toApiDate(values.dateTo),
      statuses: (values.statuses || []) as MarkingCodesKmStatus[],
      ntin: values.ntin.trim() || undefined,
      productTypeCode: values.productTypeCode.trim() || undefined,
      orderNumber: values.orderNumber.trim() || undefined,
      packagingType: (values.packagingType || undefined) as
        | MarkingCodesPackagingType
        | undefined,
    };

    if (isOperator && values.organizationId.trim()) {
      const orgId = Number(values.organizationId.trim());
      if (Number.isFinite(orgId) && orgId > 0) {
        payload.organizationId = orgId;
      }
    }

    setSubmitting(true);
    try {
      const response = await createMarkingCodesReport(payload);
      if (response?.id) {
        navigate(`/reports/marking-codes-status/${response.id}`);
      } else {
        setFormError(t("marking_codes_report.create_error"));
      }
    } catch (e: any) {
      console.error("Error creating marking codes report:", e);
      setFormError(
        e?.response?.data?.message ||
          e?.response?.data?.errorMessage ||
          t("marking_codes_report.create_error"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <h2 className="my-3 text-xl font-semibold">
        {t("marking_codes_report.create_title")}
      </h2>

      <div className="bg-white rounded-xl shadow-md p-6 mb-5">
        <h3 className="text-lg font-semibold mb-4">
          {t("billing.generationParams")}
        </h3>
        <Formik
          initialValues={{
            dateFrom: "",
            dateTo: "",
            statuses: [] as string[],
            packagingType: "",
            ntin: "",
            productTypeCode: "",
            orderNumber: "",
            organizationId: "",
            productGroupId: String(activeProductGroupId() ?? ""),
          }}
          onSubmit={handleGenerate}
        >
          {({ setFieldValue, values }) => (
            <Form>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {isOperator && (
                  <div className={`${style.inputField} md:col-span-2`}>
                    <div className={style.labelAndButtonWrapper}>
                      <label className={`${style.inputLabel} text-red-600`}>
                        {t("participants_registry_report.product_group")} *
                      </label>
                    </div>
                    <Field
                      component={SelectCustomFormik}
                      name="productGroupId"
                      isClearable
                      isMulti={false}
                      size="dm"
                      options={productGroupOptions}
                      placeholder={t("documentLabels.choose")}
                      onChange={(next: string) => {
                        setFieldValue("productGroupId", next || "");
                        // Код вида товара другой группы больше не подходит
                        setFieldValue("productTypeCode", "");
                      }}
                    />
                  </div>
                )}

                <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={`${style.inputLabel} text-red-600`}>
                      {t("marking_codes_report.km_status")} *
                    </label>
                  </div>
                  <Field
                    component={SelectCustomFormik}
                    name="statuses"
                    isMulti
                    isClearable
                    size="dm"
                    options={statusOptions}
                    placeholder={t("documentLabels.choose")}
                    onChange={(next: string[]) => {
                      setFieldValue("statuses", next || []);
                    }}
                  />
                </div>

                <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("marking_codes.col_km_type")}
                    </label>
                  </div>
                  <Field
                    component={SelectCustomFormik}
                    name="packagingType"
                    isClearable
                    size="dm"
                    options={packagingOptions}
                    placeholder={t("documentLabels.choose")}
                    onChange={(next: string) => {
                      setFieldValue("packagingType", next || "");
                    }}
                  />
                </div>

                <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("billing.periodStart")}
                    </label>
                  </div>
                  <CalendarSelectFormik
                    name="dateFrom"
                    documentStatus="new"
                    outputFormat="dd.MM.yyyy"
                    showMonthYearSelect
                    placeholder={t("components.choose")}
                  />
                </div>

                <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("billing.periodEnd")}
                    </label>
                  </div>
                  <CalendarSelectFormik
                    name="dateTo"
                    documentStatus="new"
                    outputFormat="dd.MM.yyyy"
                    showMonthYearSelect
                    placeholder={t("components.choose")}
                  />
                </div>

                <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>NTIN</label>
                  </div>
                  <Field
                    type="text"
                    name="ntin"
                    className={style.placeholder}
                    placeholder={t("documentLabels.choose_option")}
                  />
                </div>

                <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("marking_codes.col_product_type_code")}
                    </label>
                  </div>
                  <Field
                    component={SelectCustomFormik}
                    name="productTypeCode"
                    isClearable
                    isMulti={false}
                    size="dm"
                    options={productTypeOptionsFor(
                      isOperator
                        ? Number(values.productGroupId) || undefined
                        : activeProductGroupId(),
                    )}
                    placeholder={t("documentLabels.choose")}
                    onChange={(next: string) => {
                      setFieldValue("productTypeCode", next || "");
                    }}
                  />
                </div>

                <div className={style.inputField}>
                  <div className={style.labelAndButtonWrapper}>
                    <label className={style.inputLabel}>
                      {t("marking_codes_report.order_number")}
                    </label>
                  </div>
                  <Field
                    type="text"
                    name="orderNumber"
                    className={style.placeholder}
                    placeholder={t("documentLabels.choose_option")}
                  />
                </div>

                {isOperator && (
                  <>
                    <div className={style.inputField}>
                      <div className={style.labelAndButtonWrapper}>
                        <label className={style.inputLabel}>
                          {t("participants_registry_report.org_iin_bin")}
                        </label>
                      </div>
                      <Field
                        component={SelectCustomFormik}
                        name="organizationId"
                        isClearable
                        isMulti={false}
                        size="dm"
                        options={orgs.iinOptions}
                        placeholder={t("participants_registry_report.org_iin_bin_ph")}
                        onInputChange={(value: string) => orgs.setIinQuery(value || "")}
                        noOptionsMessage={orgs.noOptionsMessage}
                        onChange={(next: string) => {
                          setFieldValue("organizationId", next || "");
                          orgs.selectOrg(next, orgs.iinOptions);
                        }}
                      />
                    </div>
                    <div className={style.inputField}>
                      <div className={style.labelAndButtonWrapper}>
                        <label className={style.inputLabel}>
                          {t("participants_registry_report.org_name")}
                        </label>
                      </div>
                      <Field
                        component={SelectCustomFormik}
                        name="organizationId"
                        isClearable
                        isMulti={false}
                        size="dm"
                        options={orgs.nameOptions}
                        placeholder={t("participants_registry_report.org_name_ph")}
                        onInputChange={(value: string) => orgs.setNameQuery(value || "")}
                        noOptionsMessage={orgs.noOptionsMessage}
                        onChange={(next: string) => {
                          setFieldValue("organizationId", next || "");
                          orgs.selectOrg(next, orgs.nameOptions);
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 md:col-span-2 -mt-2">
                      {t("marking_codes_report.organization_all_hint")}
                    </p>
                  </>
                )}
              </div>

              <p className="text-xs text-gray-500 mt-2">
                {t("marking_codes_report.period_hint")}
              </p>

              {formError && (
                <p className="text-sm text-red-600 mt-3">{formError}</p>
              )}

              <div className="flex flex-row gap-[10px] justify-start mt-5">
                <button
                  type="submit"
                  className="bg-green text-white font-semibold py-2 px-4 rounded disabled:opacity-60"
                  disabled={submitting}
                >
                  {t("billing.generateReport")}
                </button>
                <button
                  type="button"
                  className="bg-bg text-gray-500 font-semibold py-2 px-4 rounded"
                  onClick={() => navigate("/reports/marking-codes-status")}
                >
                  {t("billing.close")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}

export default MarkingCodesStatusReportCreatePage;
